import axios from 'axios';
import {
  Task, TaskCreate, TaskUpdate, TaskComplete, TaskReopen, TaskCommentCreate, TaskStats,
  MonthlyTaskTemplate, MonthlyTaskTemplateCreate,
  Notification
} from '../types/api';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8103/api').replace(/\/$/, '');

// Create axios instance with base URL
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Enforce master-data scoping from frontend: auto-attach selected location when missing.
  const selectedLocationId = localStorage.getItem('selected_location_id');
  const url = config.url || '';
  const method = (config.method || 'get').toLowerCase();
  const isMasterDataRequest = url.startsWith('/master-data/');

  if (isMasterDataRequest && selectedLocationId) {
    if (method === 'get' || method === 'delete') {
      const params = (config.params || {}) as Record<string, any>;
      if (!params.location_id) {
        params.location_id = selectedLocationId;
      }
      config.params = params;
    } else if (method === 'post') {
      if (config.data && typeof config.data === 'object' && !Array.isArray(config.data)) {
        const body = config.data as Record<string, any>;
        if (!body.location_id) {
          config.data = { ...body, location_id: selectedLocationId };
        }
      }
    }
  }

  return config;
});

// Utility function to extract error message from API error response
export const getErrorMessage = (err: any, defaultMessage: string = 'An error occurred'): string => {
  if (err.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === 'string') {
      return detail;
    } else if (Array.isArray(detail)) {
      // Handle array of error objects (like FastAPI validation errors)
      const errors = detail.map((e: any) => {
        if (typeof e === 'string') {
          return e;
        } else if (typeof e === 'object' && e !== null) {
          // FastAPI validation errors have 'loc' and 'msg' fields
          if (e.msg) {
            const fieldPath = Array.isArray(e.loc) ? e.loc : [];
            // Filter out 'body' from the path and get the actual field name
            const field = fieldPath.filter((f: any) => f !== 'body' && f !== 'args' && f !== 'kwargs').slice(-1)[0] || '';
            // If field is empty but we have a path, use the last meaningful part
            const displayField = field || (fieldPath.length > 0 ? fieldPath[fieldPath.length - 1] : '');
            return displayField ? `${displayField}: ${e.msg}` : e.msg;
          }
          // Fallback: try to extract meaningful info
          if (e.message) return e.message;
          if (e.error) return String(e.error);
          // Try to extract field from loc if available
          if (Array.isArray(e.loc)) {
            const field = e.loc.filter((f: any) => f !== 'body' && f !== 'args' && f !== 'kwargs').slice(-1)[0];
            return field ? `${field}: ${e.msg || 'Field required'}` : (e.msg || JSON.stringify(e));
          }
          return JSON.stringify(e);
        }
        return String(e);
      }).filter(Boolean);
      
      // Filter out generic "args" and "kwargs" errors and show actual field errors
      const meaningfulErrors = errors.filter((e: string) => 
        !e.includes('args: Field required') && !e.includes('kwargs: Field required')
      );
      
      if (meaningfulErrors.length > 0) {
        return meaningfulErrors.join(', ');
      }
      
      // If only args/kwargs errors, try to extract actual field names from the error objects
      const fieldErrors = detail
        .filter((e: any) => e && typeof e === 'object' && Array.isArray(e.loc))
        .map((e: any) => {
          const field = e.loc.filter((f: any) => f !== 'body' && f !== 'args' && f !== 'kwargs').slice(-1)[0];
          return field ? `${field}: ${e.msg || 'Field required'}` : null;
        })
        .filter(Boolean);
      
      return fieldErrors.length > 0 ? fieldErrors.join(', ') : errors.join(', ');
    } else if (typeof detail === 'object' && detail !== null) {
      if (detail.msg) return detail.msg;
      if (detail.message) return detail.message;
      if (detail.error) return String(detail.error);
      return JSON.stringify(detail);
    }
  }
  if (err.message && typeof err.message === 'string') {
    return err.message;
  }
  return defaultMessage;
};

export default apiClient;

// Task CRUD
export const createTask = async (taskData: TaskCreate): Promise<Task> => {
  const response = await apiClient.post('/tasks', taskData);
  return response.data;
};

export const getTasks = async (params?: {
  location_id?: string;
  status?: string;
  assigned_to?: string;
  priority?: string;
  category?: string;
}): Promise<Task[]> => {
  const response = await apiClient.get('/tasks', { params });
  return response.data;
};

export const getMyTasks = async (status?: string): Promise<Task[]> => {
  const response = await apiClient.get('/tasks/my-tasks', { params: { status } });
  return response.data;
};

export const getPendingApprovalTasks = async (): Promise<Task[]> => {
  const response = await apiClient.get('/tasks/pending-approval');
  return response.data;
};

export const getTask = async (taskId: string): Promise<Task> => {
  const response = await apiClient.get(`/tasks/${taskId}`);
  return response.data;
};

export const updateTask = async (taskId: string, taskData: TaskUpdate): Promise<Task> => {
  const response = await apiClient.put(`/tasks/${taskId}`, taskData);
  return response.data;
};

export const completeTask = async (taskId: string, completeData: TaskComplete): Promise<Task> => {
  const response = await apiClient.post(`/tasks/${taskId}/complete`, completeData);
  return response.data;
};

export const approveTask = async (taskId: string): Promise<Task> => {
  const response = await apiClient.post(`/tasks/${taskId}/approve`);
  return response.data;
};

export const reopenTask = async (taskId: string, reopenData: TaskReopen): Promise<Task> => {
  const response = await apiClient.post(`/tasks/${taskId}/reopen`, reopenData);
  return response.data;
};

export const addTaskComment = async (taskId: string, commentData: TaskCommentCreate): Promise<Task> => {
  const response = await apiClient.post(`/tasks/${taskId}/comments`, commentData);
  return response.data;
};

export const deleteTask = async (taskId: string): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/tasks/${taskId}`);
  return response.data;
};

export const getTaskStats = async (location_id?: string): Promise<TaskStats> => {
  const response = await apiClient.get('/tasks/stats', { params: { location_id } });
  return response.data;
};

// Monthly Template API Functions
export const createMonthlyTemplate = async (templateData: MonthlyTaskTemplateCreate): Promise<MonthlyTaskTemplate> => {
  const response = await apiClient.post('/tasks/monthly-templates', templateData);
  return response.data;
};

export const getMonthlyTemplates = async (location_id?: string): Promise<MonthlyTaskTemplate[]> => {
  const response = await apiClient.get('/tasks/monthly-templates', { params: { location_id } });
  return response.data;
};

export const updateMonthlyTemplate = async (templateId: string, templateData: MonthlyTaskTemplateCreate): Promise<MonthlyTaskTemplate> => {
  const response = await apiClient.put(`/tasks/monthly-templates/${templateId}`, templateData);
  return response.data;
};

export const deleteMonthlyTemplate = async (templateId: string): Promise<void> => {
  await apiClient.delete(`/tasks/monthly-templates/${templateId}`);
};

export const generateTasksFromTemplate = async (templateId: string): Promise<{ message: string; task_id?: string }> => {
  const response = await apiClient.post(`/tasks/monthly-templates/${templateId}/generate`);
  return response.data;
};

export const bulkCreatePondSchedule = async (location_id: string): Promise<{ message: string; templates: any[]; skipped_count?: number }> => {
  const response = await apiClient.post('/tasks/monthly-templates/bulk-create-pond-schedule', null, {
    params: { location_id }
  });
  return response.data;
};

export const triggerSystemTaskGeneration = async (): Promise<{ message: string; results: any }> => {
  const response = await apiClient.post('/tasks/generate-system-tasks');
  return response.data;
};

// Notification API Functions
export const getNotifications = async (is_read?: boolean): Promise<Notification[]> => {
  const response = await apiClient.get('/tasks/notifications', { params: { is_read } });
  return response.data;
};

export const markNotificationRead = async (notificationId: string): Promise<Notification> => {
  const response = await apiClient.put(`/tasks/notifications/${notificationId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async (): Promise<void> => {
  await apiClient.put('/tasks/notifications/read-all');
};

export const getUnreadNotificationCount = async (): Promise<{ unread_count: number }> => {
  const response = await apiClient.get('/tasks/notifications/unread-count');
  return response.data;
};
